import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";

// @ts-ignore - JS コンポーネント（型定義なし）
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
// @ts-ignore - JSX コンポーネント（型定義なし）
import TitleBlock from "../../components/block/TitleBlock";
import HelpHint from "../../components/help-hint/HelpHint";
import SvgLoadingAnimation from "../../components/loading";
import InlineLoading from "../../components/loading/InlineLoading";
import Table, { TableColumn } from "../../components/table/Table";
import Input from "../../components/form-item/Input";
import Button from "../../components/button/Button";
import ConfirmModal from "../../components/modal/ConfirmModal";
import ToolTip from "../../components/tooltip/ToolTip";
import CollapsedAvatarStack from "../../components/avatar-stack/CollapsedAvatarStack";
import DropdownMenu from "../../components/dropdown-menu/DropdownMenu";
import DateDisplay, {
  DATE_DISPLAY_TYPE,
} from "../../components/date-display/DateDisplay";
import {
  useGetAdminProjectsQuery,
  useDeleteAdminProjectMutation,
  type AdminProjectMemberSummary,
  type AdminProjectRow,
  type AdminProjectSortField,
} from "../../app/services/adminProjects";
import { orderMembersForStack } from "../../utils/memberPositionUtils";
import ProjectMembersModal from "./components/ProjectMembersModal";

// ===== 定数 =====

const PAGE_LIMIT = 30;
const SEARCH_DEBOUNCE_MS = 300;
/** メンバーシップセルに並べるアバターの最大数（残りは +N 表示） */
const MEMBER_AVATAR_LIMIT = 3;

type SortState = { field: AdminProjectSortField; order: "asc" | "desc" } | null;

// ===== styled-components =====

// アカウント管理と同じ幅制限なしのフルワイドレイアウト。
const PageWrapper = styled.div.attrs({
  className: "u-col u-w100 u-gap16",
})`
  padding: 24px 16px 80px;
`;

const SearchBar = styled.div`
  width: 100%;
  max-width: 400px;
`;

const EmptyCell = styled.td.attrs({
  className: "bodytext-13 u-text-light-gray u-text-center",
})`
  padding: 32px 8px !important;
`;

const Sentinel = styled.div`
  width: 100%;
  height: 1px;
`;

// ===== hooks =====

const useDebouncedValue = (value: string, delay: number): string => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
};

/**
 * 検索 + 無限スクロール対応のプロジェクト一覧フック。
 * 検索語・ソート変更でローカル累積をリセットする（Account の
 * useAccountsPagination と同パターン）。
 */
const useAdminProjectsPagination = ({
  q,
  sort,
}: {
  q: string;
  sort: SortState;
}) => {
  const [offset, setOffset] = useState(0);
  const [accumulated, setAccumulated] = useState<AdminProjectRow[]>([]);

  const queryArg = useMemo(
    () => ({
      q: q || undefined,
      limit: PAGE_LIMIT,
      offset,
      sort: sort?.field,
      order: sort?.order,
    }),
    [q, offset, sort],
  );
  const result = useGetAdminProjectsQuery(queryArg);

  useEffect(() => {
    setOffset(0);
    setAccumulated([]);
  }, [q, sort]);

  // ページ取得結果を累積（offset=0 のときは置き換え、それ以外は追記）。
  useEffect(() => {
    const data = result.data?.data;
    if (!data) return;
    setAccumulated((prev) => {
      if (data.offset === 0) return data.projects;
      const seen = new Set(prev.map((p) => p.project_id));
      return [
        ...prev,
        ...data.projects.filter((p) => !seen.has(p.project_id)),
      ];
    });
  }, [result.data]);

  const hasNext = !!result.data?.data?.has_next;
  const isFetching = result.isFetching;

  const loadMore = useCallback(() => {
    if (!hasNext || isFetching) return;
    setOffset((prev) => prev + PAGE_LIMIT);
  }, [hasNext, isFetching]);

  // 削除成功後にロード済み行をローカル除去する
  // （一覧を再フェッチすると無限スクロールの累積がリセットされるため）。
  const removeProject = useCallback((projectId: string) => {
    setAccumulated((prev) => prev.filter((p) => p.project_id !== projectId));
  }, []);

  // メンバーシップモーダルでの操作（権限変更・退出）をロード済み行へ反映する。
  const patchProject = useCallback(
    (projectId: string, patch: Partial<AdminProjectRow>) => {
      setAccumulated((prev) =>
        prev.map((p) => (p.project_id === projectId ? { ...p, ...patch } : p)),
      );
    },
    [],
  );

  return {
    projects: accumulated,
    isLoading: result.isLoading,
    isFetching,
    isError: result.isError,
    hasNext,
    loadMore,
    removeProject,
    patchProject,
  };
};

// ===== helpers =====

const getProjectDisplayName = (project: AdminProjectRow): string =>
  project.code_name || project.name || `プロジェクトID ${project.project_id}`;

/** 総費用（Decimal 文字列）を円表示にする */
const formatCost = (totalCost: string): string => {
  const value = Number(totalCost);
  if (!Number.isFinite(value)) return totalCost;
  return `¥${Math.trunc(value).toLocaleString()}`;
};

// ===== メインコンポーネント =====

/**
 * プロジェクト管理ページ（/console/projects、ADMIN = master_admin のみ）。
 *
 * - 集計列付きの全プロジェクト一覧（コードネーム・最終更新日・メンバーシップ・
 *   ショット数・総ショット数・総メッセージ数・DM スレッド数・総費用）。
 * - メンバーシップセルのクリックでメンバー一覧モーダル
 *   （バナーの責任者と同じメンバー操作: 権限変更・IP制限・退出）。
 * - 列ヘッダークリックでサーバーサイドソート（昇順 → 降順 → 既定順）。
 * - 最右列の 3 点リーダー → 削除（確認モーダル → 論理削除）。
 */
const AdminProjects: React.FC = () => {
  const { data: currentUser, isLoading: isUserLoading } =
    useGetCurrentUserQuery();

  const role: string | undefined = currentUser?.data?.role;
  const isAdmin = role === "ADMIN";

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  // 列ヘッダーのサーバーサイドソート（昇順 → 降順 → 既定順の 3 状態トグル）
  const [sortState, setSortState] = useState<SortState>(null);
  const handleSort = useCallback((field: AdminProjectSortField) => {
    setSortState((prev) => {
      if (prev?.field !== field) return { field, order: "asc" };
      if (prev.order === "asc") return { field, order: "desc" };
      return null; // 3 回目のクリックでサーバー既定順（最終更新日降順）へ戻す
    });
  }, []);

  const {
    projects: rows,
    isLoading,
    isFetching,
    isError,
    hasNext,
    loadMore,
    removeProject,
    patchProject,
  } = useAdminProjectsPagination({
    q: debouncedSearch,
    sort: sortState,
  });

  // メンバーシップモーダル
  const [membersTarget, setMembersTarget] = useState<AdminProjectRow | null>(
    null,
  );
  // モーダル内の操作（権限変更・退出）をテーブル行のスタック・人数へ反映
  const handleMembersChanged = useCallback(
    (projectId: string, members: AdminProjectMemberSummary[]) => {
      patchProject(projectId, { members, member_count: members.length });
    },
    [patchProject],
  );

  // 削除確認モーダル
  const [deleteTarget, setDeleteTarget] = useState<AdminProjectRow | null>(
    null,
  );
  const [deleteAdminProject, { isLoading: isDeleting }] =
    useDeleteAdminProjectMutation();
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteAdminProject(deleteTarget.project_id).unwrap();
      removeProject(deleteTarget.project_id);
      toast.success(
        `「${getProjectDisplayName(deleteTarget)}」を削除しました`,
      );
      setDeleteTarget(null);
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message;
      toast.error(message || "プロジェクトの削除に失敗しました");
    }
  }, [deleteTarget, deleteAdminProject, removeProject]);

  // 無限スクロール
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMore();
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  if (isUserLoading) {
    return <SvgLoadingAnimation />;
  }

  if (!isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  // ソート可能ヘッダー（Account と同じ見た目）
  const sortHeader = (label: string, field: AdminProjectSortField) => (
    <>
      <span>{label}</span>
      {sortState?.field === field && (
        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
          {sortState.order === "asc" ? "arrow_upward" : "arrow_downward"}
        </span>
      )}
    </>
  );

  /** ソート可能列の共通定義（ヘッダークリックでサーバーソート） */
  const sortableColumn = (
    field: AdminProjectSortField,
    label: string,
    extra?: Partial<TableColumn>,
  ): TableColumn => ({
    key: field,
    header: sortHeader(label, field),
    onHeaderClick: () => handleSort(field),
    headerTitle: "クリックでソート",
    ...extra,
  });

  const columns: TableColumn[] = [
    sortableColumn("code_name", "コードネーム", { minWidth: 140 }),
    sortableColumn("updated_at", "最終更新日", { width: 120, minWidth: 100 }),
    sortableColumn("member_count", "メンバーシップ", {
      width: 160,
      minWidth: 130,
    }),
    sortableColumn("submitted_shots_count", "ショット数", {
      width: 96,
      minWidth: 84,
    }),
    sortableColumn("total_shots_count", "総ショット数", {
      width: 108,
      minWidth: 96,
    }),
    {
      key: "total_message_count",
      header: "総メッセージ数",
      width: 120,
      minWidth: 104,
    },
    sortableColumn("dm_thread_count", "DMスレッド数", {
      width: 116,
      minWidth: 100,
    }),
    { key: "total_cost", header: "総費用", width: 120, minWidth: 100 },
    { key: "menu", header: "", width: 48, minWidth: 48, resizable: false },
  ];

  const renderRows = () => {
    if (isLoading) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            <InlineLoading visible />
          </EmptyCell>
        </tr>
      );
    }
    if (isError) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            プロジェクト一覧の取得に失敗しました。
          </EmptyCell>
        </tr>
      );
    }
    if (rows.length === 0) {
      return (
        <tr>
          <EmptyCell colSpan={columns.length}>
            {debouncedSearch
              ? "該当するプロジェクトが見つかりません"
              : "プロジェクトがありません"}
          </EmptyCell>
        </tr>
      );
    }
    return rows.map((project) => {
      // プロジェクトバナーのスタックと同じ並び
      // （クライアント → プロデューサー → スペシャリスト）に整列する。
      const stackItems = orderMembersForStack(project.members).map(
        (member) => ({
          key: member.user_id,
          userId: member.user_id,
          avatarUrl: member.avatar_url,
          displayName: member.display_name_jp || member.display_name_en,
        }),
      );
      return (
        <tr key={project.project_id}>
          <ToolTip
            description={project.name || project.code_name}
            truncatedOnly
          >
            <td className="bodytext-13">{getProjectDisplayName(project)}</td>
          </ToolTip>
          <td className="bodytext-13 u-text-light-gray">
            {project.updated_at ? (
              <DateDisplay
                date={project.updated_at}
                type={DATE_DISPLAY_TYPE.DATE_TIME}
              />
            ) : (
              "—"
            )}
          </td>
          {/* table-cell-visible: アバターの影とポップオーバーを
              セルの overflow: hidden から解放する */}
          <td className="table-cell-visible">
            {stackItems.length > 0 ? (
              <Button
                size="small"
                variant="text"
                width="fit"
                onClick={() => setMembersTarget(project)}
                aria-label={`${getProjectDisplayName(project)} のメンバー一覧（${project.member_count}人）を開く`}
              >
                {/* バナーと同じ +N 集約スタック（グローバルコンポーネント） */}
                <CollapsedAvatarStack
                  items={stackItems}
                  size={24}
                  maxVisible={MEMBER_AVATAR_LIMIT}
                  totalCount={project.member_count}
                  align="start"
                />
              </Button>
            ) : (
              <span className="bodytext-13 u-text-light-gray">0人</span>
            )}
          </td>
          <td className="bodytext-13 u-text-right">
            {project.submitted_shots_count.toLocaleString()}
          </td>
          <td className="bodytext-13 u-text-right">
            {project.total_shots_count.toLocaleString()}
          </td>
          <td className="bodytext-13 u-text-right">
            {project.total_message_count.toLocaleString()}
          </td>
          <td className="bodytext-13 u-text-right">
            {project.dm_thread_count.toLocaleString()}
          </td>
          {project.cost_source === "reward" ? (
            <ToolTip description="旧環境の報酬集計（管理者発注の合計）による概算です">
              <td className="bodytext-13 u-text-right">
                {formatCost(project.total_cost)}
              </td>
            </ToolTip>
          ) : (
            <td className="bodytext-13 u-text-right">
              {formatCost(project.total_cost)}
            </td>
          )}
          {/* table-cell-visible: Tippy ポップオーバーのクリップを解除 */}
          <td className="table-cell-visible" style={{ textAlign: "center" }}>
            <DropdownMenu
              size="small"
              items={[
                {
                  label: "削除",
                  icon: "delete",
                  onClick: () => setDeleteTarget(project),
                },
              ]}
            />
          </td>
        </tr>
      );
    });
  };

  return (
    <PageWrapper>
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="PROJECT MANAGEMENT" />
        <HelpHint description={"全プロジェクトの状態を確認し、不要になったプロジェクトを削除します。総費用は再委託を重複カウントしない循環金額です。"} />
      </div>

      <SearchBar>
        <Input
          type="search"
          placeholder="コードネーム・作品名で検索"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </SearchBar>

      <Table columns={columns} minWidth={1028}>
        {renderRows()}
      </Table>

      {/* 無限スクロールの監視要素 + 追加ロード表示 */}
      <Sentinel ref={sentinelRef} />
      {isFetching && !isLoading && <InlineLoading visible />}
      {!hasNext && !isLoading && rows.length > 0 && (
        <p className="bodytext-11 u-text-light-gray u-text-center u-w100">
          {rows.length} 件を表示中
        </p>
      )}

      {/* メンバー一覧モーダル */}
      {membersTarget && (
        <ProjectMembersModal
          project={membersTarget}
          onClose={() => setMembersTarget(null)}
          onMembersChanged={handleMembersChanged}
        />
      )}

      {/* プロジェクト削除確認 */}
      {deleteTarget && (
        <ConfirmModal
          message={
            `「${getProjectDisplayName(deleteTarget)}」を削除しますか？\n` +
            "プロジェクトは全メンバーから見えなくなります。この操作は取り消せません。"
          }
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </PageWrapper>
  );
};

export default AdminProjects;
